import { Injectable } from '@angular/core';
import { RuleGeneratorService } from './rule-generator.service';
import { ItemDataService } from '../item-data.service';
import { poe1, poe2 } from '../../../game';
import { Comparator, DisplayType, GearSizeType, LabelSize, Rarity, VendoringGearType, YesNoAny } from '../../models/enums';
import { AdvancedConditions, CosmeticOptions, CustomRule, DisplayOptions, Filter } from '../../models/filter-models';

@Injectable({
  providedIn: 'root'
})
export class HiddenItemsGeneratorService {
  private allGearTypes!: string;
  private itemColors!: { normal: string, magic: string, rare: string };
  private hiddenItemColors!: { normal: string, magic: string, rare: string };
  private hiddenBackgroundColor!: string;

  private hiddenDisplayOptionsNormal!: DisplayOptions;
  private hiddenDisplayOptionsMagic!: DisplayOptions;
  private hiddenDisplayOptionsRare!: DisplayOptions;

  private hiddenDisplayOptionsUnstyled!: DisplayOptions;

  private remainingHideAdvancedConditions = new AdvancedConditions({ identified: YesNoAny.No });

  constructor(private ruleGenerator: RuleGeneratorService, private itemDataService: ItemDataService) {
    this.allGearTypes = this.itemDataService.getAllGearTypes().map(g => `"${g}"`).join(' ');
    this.itemColors = itemDataService.getItemColors();
    this.hiddenItemColors = {
      normal: this.itemColors.normal.replace(')', ',0.6)'),
      magic: this.itemColors.magic.replace(')', ',0.6)'),
      rare: this.itemColors.rare.replace(')', ',0.6)'),
    };
    this.hiddenBackgroundColor = 'rgba(0, 0, 0, 0.6)';

    this.hiddenDisplayOptionsNormal = new DisplayOptions({
      hidden: true,
      displayType: DisplayType.Custom,
      cosmeticOptions: new CosmeticOptions({
        fontSize: LabelSize.Smallest,
        textColor: this.hiddenItemColors.normal,
        backgroundColor: this.hiddenBackgroundColor,
      })
    });

    this.hiddenDisplayOptionsMagic = new DisplayOptions({
      hidden: true,
      displayType: DisplayType.Custom,
      cosmeticOptions: new CosmeticOptions({
        fontSize: LabelSize.Smallest,
        textColor: this.hiddenItemColors.magic,
        backgroundColor: this.hiddenBackgroundColor,
      })
    });

    this.hiddenDisplayOptionsRare = new DisplayOptions({
      hidden: true,
      displayType: DisplayType.Custom,
      cosmeticOptions: new CosmeticOptions({
        fontSize: LabelSize.Smallest,
        textColor: this.hiddenItemColors.rare,
        backgroundColor: this.hiddenBackgroundColor,
      })
    });

    this.hiddenDisplayOptionsUnstyled = new DisplayOptions({
      hidden: true,
    });
  }

  generateHiddenGearRules(filter: Filter): string {
    let hiddenGearRules = '';

    if (filter.showVendoringRares) {
      let size = '';

      if (filter.showVendoringRaresSize === GearSizeType.TwoByTwoOnly) {
        size = `
  Width <= 2
  Height <= 2`;
      } else if (filter.showVendoringRaresSize === GearSizeType.MediumOnly) {
        size = `
  Width <= 2
  Height <= 3`;
      } else if (filter.showVendoringRaresSize === GearSizeType.JewelleryOnly) {
        size = `
  Height == 1`;
      }

      const rareAdvancedConditions = new AdvancedConditions({ maxAreaLevel: filter.showVendoringRaresUntilAreaLevel });

      if (filter.showVendoringRaresType === VendoringGearType.PreventTransparentHidden) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          filterItemClass: this.allGearTypes,
          displayOptions: this.hiddenDisplayOptionsUnstyled,
          advancedConditions: rareAdvancedConditions
        }), filter, 'gear-hidden-vendoring-rares', 'Vendoring Rare Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowDefault) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          filterItemClass: this.allGearTypes,
          displayOptions: new DisplayOptions(),
          advancedConditions: rareAdvancedConditions
        }), filter, 'gear-hidden-vendoring-rares', 'Vendoring Rare Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowTransparent) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          filterItemClass: this.allGearTypes,
          displayOptions: { ...this.hiddenDisplayOptionsRare, hidden: false },
          advancedConditions: rareAdvancedConditions
        }), filter, 'gear-hidden-vendoring-rares', 'Vendoring Rare Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          filterItemClass: this.allGearTypes,
          displayOptions: new DisplayOptions(),
          advancedConditions: rareAdvancedConditions
        }), filter, 'gear-hidden-vendoring-rares', 'Vendoring Rare Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowStyled) {
        const options = filter.customRuleStyles.find(s => s.ruleId === 'gear-hidden-vendoring-rares')!.options;
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          filterItemClass: this.allGearTypes,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options }),
          advancedConditions: rareAdvancedConditions
        }), filter, 'gear-hidden-vendoring-rares', 'Vendoring Rare Items', { customLine: size });
      }

      hiddenGearRules += "\n\n";
    }

    if (filter.showVendoringMagics) {
      let size = '';

      if (filter.showVendoringMagicsSize === GearSizeType.TwoByTwoOnly) {
        size = `
  Width <= 2
  Height <= 2`;
      } else if (filter.showVendoringMagicsSize === GearSizeType.MediumOnly) {
        size = `
  Width <= 2
  Height <= 3`;
      } else if (filter.showVendoringMagicsSize === GearSizeType.JewelleryOnly) {
        size = `
  Height == 1`;
      }

      const magicAdvancedConditions = new AdvancedConditions({ maxAreaLevel: filter.showVendoringMagicsUntilAreaLevel });

      if (filter.showVendoringRaresType === VendoringGearType.PreventTransparentHidden) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          filterItemClass: this.allGearTypes,
          displayOptions: this.hiddenDisplayOptionsUnstyled,
          advancedConditions: magicAdvancedConditions
        }), filter, 'gear-hidden-vendoring-magic', 'Vendoring Magic Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowDefault) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          filterItemClass: this.allGearTypes,
          displayOptions: new DisplayOptions(),
          advancedConditions: magicAdvancedConditions
        }), filter, 'gear-hidden-vendoring-magic', 'Vendoring Magic Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowTransparent) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          filterItemClass: this.allGearTypes,
          displayOptions: { ...this.hiddenDisplayOptionsMagic, hidden: false },
          advancedConditions: magicAdvancedConditions
        }), filter, 'gear-hidden-vendoring-magic', 'Vendoring Magic Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares) {
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          filterItemClass: this.allGearTypes,
          displayOptions: new DisplayOptions(),
          advancedConditions: magicAdvancedConditions
        }), filter, 'gear-hidden-vendoring-magic', 'Vendoring Magic Items', { customLine: size });
      } else if (filter.showVendoringRaresType === VendoringGearType.ShowStyled) {
        const options = filter.customRuleStyles.find(s => s.ruleId === 'gear-hidden-vendoring-magic')!.options;
        hiddenGearRules += this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          filterItemClass: this.allGearTypes,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options }),
          advancedConditions: magicAdvancedConditions
        }), filter, 'gear-hidden-vendoring-magic', 'Vendoring Magic Items', { customLine: size });
      }

      hiddenGearRules += "\n\n";
    }

    if (filter.hideAllOtherGear || filter.showLevelingGear) {
      hiddenGearRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Normal,
        filterItemClass: this.allGearTypes,
        displayOptions: filter.hiddenGearSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'gear-hide-remaining', 'Normal Gear');

      hiddenGearRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Magic,
        filterItemClass: this.allGearTypes,
        displayOptions: filter.hiddenGearSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'gear-hide-remaining', 'Magic Gear');

      hiddenGearRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Rare,
        filterItemClass: this.allGearTypes,
        displayOptions: filter.hiddenGearSmallAndTransparent ? this.hiddenDisplayOptionsRare : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'gear-hide-remaining', 'Rare Gear');
    }

    return hiddenGearRules;
  }

  generateHiddenJewelleryRules(filter: Filter): string {
    let hiddenJewelleryRules = '';

    if (filter.hideAmulets) {
      const options = poe2 ? undefined : {
        customBaseTypes: '!= "Talisman"'
      };

      hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Normal,
        itemClass: 'Amulets',
        displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'jewellery-hidden-amulets-normal', 'Normal Amulets', options);

      if (filter.hideAmuletsOfRarity === Rarity.Magic || filter.hideAmuletsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          itemClass: 'Amulets',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewellery-hidden-amulets-magic', 'Magic Amulets', options);
      }

      if (filter.hideAmuletsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          itemClass: 'Amulets',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsRare : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewellery-hidden-amulets-rare', 'Rare Amulets', options);
      }
    }

    if (poe1 && filter.hideTalismans) {
      hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Rare,
        itemClass: 'Amulets',
        displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsRare : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'jewellery-hidden-talismans', 'Talismans', { customBaseTypes: '"Talisman"' });
    }

    if (filter.hideRings) {
      hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Normal,
        itemClass: 'Rings',
        displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'jewellery-hidden-rings-normal', 'Normal Rings');

      if (filter.hideRingsOfRarity === Rarity.Magic || filter.hideRingsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          itemClass: 'Rings',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewellery-hidden-rings-magic', 'Magic Rings');
      }

      if (filter.hideRingsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          itemClass: 'Rings',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsRare : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewellery-hidden-rings-rare', 'Rare Rings');
      }
    }

    if (filter.hideBelts) {
      hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Normal,
        itemClass: 'Belts',
        displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'jewellery-hidden-belts-normal', 'Normal Belts');

      if (filter.hideBeltsOfRarity === Rarity.Magic || filter.hideBeltsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          itemClass: 'Belts',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewellery-hidden-belts-magic', 'Magic Belts');
      }

      if (filter.hideBeltsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          itemClass: 'Belts',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsRare : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewellery-hidden-belts-rare', 'Rare Belts');
      }
    }

    if (filter.hideJewels) {
      hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Normal,
        filterItemClass: poe2 ? '"Jewels"' : '"Jewels" "Abyss Jewels"',
        displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled,
        advancedConditions: this.remainingHideAdvancedConditions,
      }), filter, 'jewels-hidden-normal', 'Normal Jewels');

      if (filter.hideJewelsOfRarity === Rarity.Magic || filter.hideJewelsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Magic,
          filterItemClass: poe2 ? '"Jewels"' : '"Jewels" "Abyss Jewels"',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewels-hidden-magic', 'Magic Jewels');
      }

      if (filter.hideJewelsOfRarity === Rarity.Rare) {
        hiddenJewelleryRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Rare,
          filterItemClass: poe2 ? '"Jewels"' : '"Jewels" "Abyss Jewels"',
          displayOptions: filter.hiddenJewellerySmallAndTransparent ? this.hiddenDisplayOptionsRare : this.hiddenDisplayOptionsUnstyled,
          advancedConditions: this.remainingHideAdvancedConditions,
        }), filter, 'jewels-hidden-rare', 'Rare Jewels');
      }
    }

    return hiddenJewelleryRules;
  }

  generateHiddenFlasksRules(filter: Filter): string {
    let hiddenFlasksRules = '';

    if (filter.hideManaFlasks) {
      hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarity: Rarity.Normal,
        rarityComparator: Comparator.Equal,
        itemClass: 'Mana Flasks',
        displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled
      }), filter, 'flasks-hidden-mana-normal', 'Normal Mana Flasks');

      hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarity: Rarity.Magic,
        rarityComparator: Comparator.Equal,
        itemClass: 'Mana Flasks',
        displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled
      }), filter, 'flasks-hidden-mana-magic', 'Magic Mana Flasks');
    }

    if (filter.hideLifeFlasks) {
      hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarity: Rarity.Normal,
        rarityComparator: Comparator.Equal,
        itemClass: 'Life Flasks',
        displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled
      }), filter, 'flasks-hidden-life-normal', 'Normal Life Flasks');

      hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
        startWithClass: true,
        rarity: Rarity.Magic,
        rarityComparator: Comparator.Equal,
        itemClass: 'Life Flasks',
        displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled
      }), filter, 'flasks-hidden-life-magic', 'Magic Life Flasks');
    }

    if (poe1) {
      if (filter.hideHybridFlasks) {
        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Normal,
          rarityComparator: Comparator.Equal,
          itemClass: 'Hybrid Flasks',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled
        }), filter, 'flasks-hidden-hybrid-normal', 'Normal Hybrid Flasks');

        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Magic,
          rarityComparator: Comparator.Equal,
          itemClass: 'Hybrid Flasks',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled
        }), filter, 'flasks-hidden-hybrid-magic', 'Magic Hybrid Flasks');
      }

      if (filter.hideUtilityFlasks) {
        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Normal,
          rarityComparator: Comparator.Equal,
          itemClass: 'Utility Flasks',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled
        }), filter, 'flasks-hidden-utility-normal', 'Normal Utility Flasks');

        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Magic,
          rarityComparator: Comparator.Equal,
          itemClass: 'Utility Flasks',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled
        }), filter, 'flasks-hidden-utility-magic', 'Magic Utility Flasks');
      }

      if (filter.hideTinctures) {
        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Normal,
          rarityComparator: Comparator.Equal,
          itemClass: 'Tinctures',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled
        }), filter, 'flasks-hidden-tinctures-normal', 'Normal Tinctures');

        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Magic,
          rarityComparator: Comparator.Equal,
          itemClass: 'Tinctures',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled
        }), filter, 'flasks-hidden-tinctures-magic', 'Magic Tinctures');
      }
    } else {
      if (filter.hideCharms) {
        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Normal,
          rarityComparator: Comparator.Equal,
          itemClass: 'Charms',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsNormal : this.hiddenDisplayOptionsUnstyled
        }), filter, 'charms-hidden-normal', 'Normal Charms');

        hiddenFlasksRules += '\n' + this.ruleGenerator.generateRule(new CustomRule({
          startWithClass: true,
          rarity: Rarity.Magic,
          rarityComparator: Comparator.Equal,
          itemClass: 'Charms',
          displayOptions: filter.hiddenFlaskSmallAndTransparent ? this.hiddenDisplayOptionsMagic : this.hiddenDisplayOptionsUnstyled
        }), filter, 'charms-hidden-magic', 'Magic Charms');
      }
    }

    return hiddenFlasksRules;
  }
}
